import React, { useMemo, useEffect } from 'react';

import {
  updateCurrentShortTurnConfig,
  handleUsesShortTurnToggle
} from './shortTurnHelpers';

import { type UserConfigurationState, type ShortTurnConfig as ShortTurnConfigType } from '@/types';
import { initialOnboardingState } from '@/constants/initialState';
import { toast } from 'sonner';

import { useOptionalFieldValidation } from '@/components/validation';
import { useDefaultExtendedTiers } from '@/hooks/useDefaultExtendedTiers';
import { useUserConfigState, useUserConfigDispatch } from '@/context/UserConfigContext';

import { ShortTurnConfigForm } from '@/components/config_forms/short_turn/ShortTurnConfigForm';
import { cn } from '@/lib/utils';

interface ShortTurnConfigProps {
  noCard?: boolean;
  hideToggle?: boolean;
  roles?: UserConfigurationState['roles'];
  rateMultipliers?: UserConfigurationState['rateMultipliers'];
}

export const ShortTurnConfig: React.FC<ShortTurnConfigProps> = ({
  noCard,
  hideToggle,
  roles: propRoles,
  rateMultipliers: propRateMultipliers,
}) => {
  const state = useUserConfigState();
  const { updateBillingConfig } = useUserConfigDispatch();
  const { billingStructureConfig } = useUserConfigState();
  const defaultExtendedTiers = useDefaultExtendedTiers('shortTurn');

  const roles = propRoles ?? state.roles;
  const currentShortTurnConfig = state.shortTurnConfig ?? initialOnboardingState.shortTurnConfig!;

  const hasMultipliersWithThresholds = useMemo(() => {
    return (state.rateMultipliers ?? []).some(m =>
      m.code !== 'STRAIGHT_TIME' &&
      (m.defaultPerShiftThresholdHours != null || m.defaultPerWeekThresholdHours != null)
    );
  }, [state.rateMultipliers]);

  const hasAdditionalMultipliers = state.rateMultipliers.some(m => m.code !== "STRAIGHT_TIME");

  useEffect(() => {
    if (state.usesShortTurn &&
        (currentShortTurnConfig.surchargeType === 'carry_over' || currentShortTurnConfig.surchargeType === 'bump_up') &&
        !hasMultipliersWithThresholds) {

      const newSurchargeType = hasAdditionalMultipliers ? 'offset_overtime' : 'fixed_dollar';

      updateCurrentShortTurnConfig(currentShortTurnConfig, updateBillingConfig, { surchargeType: newSurchargeType });

      toast.info("Short turn surcharge method updated.", {
        description: "Your previous method is unavailable because no multipliers with thresholds are defined.",
      });
    }
  }, [state.usesShortTurn, currentShortTurnConfig.surchargeType, hasMultipliersWithThresholds, hasAdditionalMultipliers, updateBillingConfig, currentShortTurnConfig]);

  // Field validations via ValidationProvider schemas
  const breakHours = useOptionalFieldValidation('shortTurnConfig.requiredBreakHours');
  const fixedSurcharge = useOptionalFieldValidation('shortTurnConfig.shortTurnFixedSurchargeAmount');
  const premiumVal = useOptionalFieldValidation('shortTurnConfig.premiumValue');

  const buildInputProps = (
    fieldKey: string,
    value: number | null,
    onChange: (value: number | null) => void,
    validation: ReturnType<typeof useOptionalFieldValidation>,
  ): Record<string, unknown> => ({
    'data-validation-key': fieldKey,
    value: value ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value ? parseFloat(e.target.value) : null;
      onChange(v);
    },
    onBlur: validation.onBlur,
    className: cn(validation.hasError && "border-destructive focus-visible:ring-destructive focus-visible:border-destructive"),
  });

  const invalidMode = !currentShortTurnConfig.surchargeType || (['carry_over', 'bump_up', 'offset_overtime'].includes(currentShortTurnConfig.surchargeType || '') && !hasAdditionalMultipliers);
  const modeError = state.usesShortTurn && invalidMode;
  const noMultiplierSelected = false; // fixed_offset uses offset amount, not a multiplier code

  return (
    <ShortTurnConfigForm
      usesShortTurn={state.usesShortTurn}
      onUsesShortTurnChange={(checked) => handleUsesShortTurnToggle(currentShortTurnConfig, state.rateMultipliers, updateBillingConfig, checked)}
      roles={roles}
      shortTurnConfig={currentShortTurnConfig}
      onShortTurnConfigChange={(updates) => updateCurrentShortTurnConfig(currentShortTurnConfig, updateBillingConfig, updates)}
      rateMultipliers={propRateMultipliers ?? state.rateMultipliers}
      hasAdditionalMultipliers={hasAdditionalMultipliers}
      minimumBillableHours={billingStructureConfig?.minimumBillableHours}
      defaultBreakInputProps={buildInputProps(
        'shortTurnConfig.requiredBreakHours',
        currentShortTurnConfig.requiredBreakHours,
        (val) => updateCurrentShortTurnConfig(currentShortTurnConfig, updateBillingConfig, { requiredBreakHours: val }),
        breakHours,
      )}
      defaultBreakError={breakHours.errorElement}
      shortTurnFixedSurchargeAmountProps={buildInputProps(
        'shortTurnConfig.shortTurnFixedSurchargeAmount',
        currentShortTurnConfig.shortTurnFixedSurchargeAmount,
        (val) => updateCurrentShortTurnConfig(currentShortTurnConfig, updateBillingConfig, { shortTurnFixedSurchargeAmount: val }),
        fixedSurcharge,
      )}
      shortTurnFixedSurchargeAmountError={fixedSurcharge.errorElement}
      shortTurnPremiumValueProps={buildInputProps(
        'shortTurnConfig.premiumValue',
        currentShortTurnConfig.premiumValue ?? null,
        (v) => updateCurrentShortTurnConfig(currentShortTurnConfig, updateBillingConfig, { premiumValue: v }),
        premiumVal,
      )}
      shortTurnPremiumValueError={premiumVal.errorElement}
      context="user"
      shortTurnApplicableRoleIds={currentShortTurnConfig.applicableRoleIds}
      onShortTurnApplicableRoleIdsChange={(roleIds) =>
        updateCurrentShortTurnConfig(currentShortTurnConfig, updateBillingConfig, { applicableRoleIds: roleIds })
      }
      noCard={noCard}
      hideToggle={hideToggle}
      defaultExtendedTiers={defaultExtendedTiers}
    />
  );
};
